import React, { useEffect, useMemo, useRef, useState } from "react";
import { clone, isEmpty, isNil } from "ramda";
import { scaleLinear, interpolateRgb } from "d3";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
  useMap,
  useMapEvents,
  Polygon,
  Pane,
  Circle,
} from "react-leaflet";
import { Icon } from "leaflet";
import "leaflet/dist/leaflet.css";
import { Autocomplete, Checkbox, Collapse, FormControlLabel, IconButton, MenuItem, TextField } from "@mui/material";
import L from "leaflet";
import "leaflet-rotate";
import palette from "../../../../../config/colorPalette";
import AlertPopup from "../AlertPopup";
import Legend from "../Leyend";
import MapTypeSelector from "../MapTypeSelector";
import {
  alarmsQuantity,
  asociarNumeroConLista,
  computeAlerts,
  //getBoundaries,
  getCentroid,
  getLineColor,
  printEquipments,
  printPolylineAlerts,
  selectEquipment,
  selectEquipmentInAssetReceiving,
  shouldAddAccordingToMeasure,
  shouldAddAccordingToState,
} from "./utils/utils";

import { LegendContent, LegendContentRight, MapTypeContent } from "./styled";
import "./styles.css";
import { useNavigate } from "react-router-dom";
import { getAssetsValuesByAssets, getPointsByAssets, getShipTrackingHistorial, simulateShip } from "../../../../api";
import { setAssetsPointsData } from "../../../../redux/assetsPointsSlice";
import { setAssetsData } from "../../../../redux/assetsValuesSlice";
import { toast } from "react-toastify";
import { createWebSocket, onCloseWS, onMessageWS, onOpenWS } from "../../../../ws";
import { getFromCookie } from "../../../../api/auth";
import PolylineIcon from "@mui/icons-material/Polyline";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import ExploreOffIcon from "@mui/icons-material/ExploreOff";
const customOkIcon = new Icon({
  iconUrl: require("../../../../../images/Uptech-marker-alerts-icon-ok.png"),
  iconSize: [50, 50],
  iconAnchor: [25, 50],
});
const customLowIcon = new Icon({
  iconUrl: require("../../../../../images/Uptech-marker-alerts-icon-low.png"),
  iconSize: [50, 50],
  iconAnchor: [25, 50],
});
const customIntermediateIcon = new Icon({
  iconUrl: require("../../../../../images/Uptech-marker-alerts-icon-intermediate.png"),
  iconSize: [50, 50],
  iconAnchor: [25, 50],
});
const customIcon = new Icon({
  iconUrl: require("../../../../../images/Uptech-marker-alerts-icon-high.png"),
  iconSize: [50, 50],
  iconAnchor: [25, 50],
});

const icons = [customOkIcon, customLowIcon, customIntermediateIcon, customIcon];

const customEquipmentAlertIcon = new Icon({
  iconUrl: require("../../../../../images/Uptech-equipment-marker-alerts-icon-new.png"),
  iconSize: [50, 50],
});

const customEquipmentIcon = new Icon({
  iconUrl: require("../../../../../images/Uptech-equipment-marker-icon.png"),
  iconSize: [50, 50],
});

const incIcon = new Icon({
  iconUrl: require("../../../../../images/Uptech-marker-icon-inclinometer.png"),
  iconSize: [50, 50],
  iconAnchor: [25, 50],
});
const createAlertIcon = (severity, type) =>
  L.divIcon({
    className: "",
    html: `
    <img 
      src="${require(`../../../../../images/Uptech-marker-alerts-icon-${severity}-${type}.png`)}" 
      style="width: 50px; height: 50px;" 
    />
  `,
    iconSize: [50, 50],
    iconAnchor: [25, 50],
  });
const createAlertIconGif = (severity, type) =>
  L.divIcon({
    className: "",
    html: `
      <img 
        src="${require(`../../../../../images/bouncing_marker_${severity}_${type}.gif`)}" 
        style="width: 50px; height: 60px;" 
      />
    `,
    iconSize: [50, 60],
    iconAnchor: [25, 60],
  });
const customShadowIcon = new Icon({
  iconUrl: require("../../../../../images/yellow-shadow.png"),
  iconSize: [60, 60],
  iconAnchor: [25, 35],
});
const Map = ({
  assets,
  configData,
  userData,
  alerts,
  shipAlerts,
  mapColors,
  borders,
  setBorders,
  objectMeasures,
  equipmentTypes,
  dispatch,
  intl,
}) => {
  const navigate = useNavigate();
  const accessToken = getFromCookie("uptechAccessToken");
  const mapRef = useRef(null);
  const wsRef = useRef(null);
  const [openPopupShipId, setOpenPopupShipId] = useState(null);
  const markerRefs = useRef({});
  const [detailViewMap, setDetailViewMap] = useState({});
  const anyDetailView = useMemo(() => Object.values(detailViewMap).some(Boolean), [detailViewMap]);
  const [bearing, setBearing] = useState(0);
  const [leftOpen, setLeftOpen] = useState(false);
  const [rightOpen, setRightOpen] = useState(false);
  const [domain, setDomain] = useState([0, 0.55]);
  const [range, setRange] = useState([interpolateRgb("green")(0), interpolateRgb("red")(0.55)]);
  const [inside, setInside] = useState([]);
  const [visible, setVisible] = useState([]);
  const [points, setPoints] = useState({ data: [] });
  const [assetsValues, setAssetsValues] = useState({ data: [] });
  const [assetEquipments, setAssetEquipments] = useState([]);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [indexEquipmentChannel, setIndexEquipmentChannel] = useState(0);
  const [equipmentSubs, setEquipmentSubs] = useState([]);
  const [equipmentChannelNames, setEquipmentChannelNames] = useState([]);
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [subList, setSubList] = useState([]);
  const [selectedSubs, setSelectedSubs] = useState([]);
  const [inclinometerList, setInclinometerList] = useState([]);
  const autocompleteRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const tColorScale = scaleLinear().domain(domain).range(range);
  const [stateMagnitudes, setStateMagnitude] = useState([]);
  const differencesForTempLength = useState([
    { range: 5000, multiplier: 1 },
    { range: 15000, multiplier: 2 },
    { range: 25000, multiplier: 3 },
  ]);

  const [selectedAssetsAlertsFilter, setSelectedAssetsAlertsFilter] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [toastWarning, setToastWarning] = useState(true);
  const [polygonCritical, setPolygonCritical] = useState(null);
  const [polygonProtect, setPolygonProtect] = useState(null);
  const [polygonAvoid, setPolygonAvoid] = useState(null);
  const [polygonExtra, setPolygonExtra] = useState(null);
  const [hidePolygons, setHidePolygons] = useState(false);

  const [shipHistory, setShipHistory] = useState(null);
  const [selectedShip, setSelectedShip] = useState(null);
  const filterInitializedRef = useRef(false);
  const scales = useMemo(() => {
    const jetDomain = [];
    const jetRange = [];
    const seismicDomain = [];
    const seismicRange = [];
    if (!isNil(mapColors)) {
      for (let index = 0; index < mapColors["jet"]["blue"].length; index++) {
        jetDomain.push(mapColors["jet"]["value"][index]);
        const redJET = Math.round(mapColors["jet"]["red"][index] * 255);
        const greenJET = Math.round(mapColors["jet"]["green"][index] * 255);
        const blueJET = Math.round(mapColors["jet"]["blue"][index] * 255);
        const hexJET = rgbToHex(redJET, greenJET, blueJET);
        jetRange.push(interpolateRgb(hexJET)(mapColors["jet"]["value"][index]));
        seismicDomain.push(mapColors["seismic"]["value"][index]);
        const redSEISMIC = Math.round(mapColors["seismic"]["red"][index] * 255);
        const greenSEISMIC = Math.round(mapColors["seismic"]["green"][index] * 255);
        const blueSEISMIC = Math.round(mapColors["seismic"]["blue"][index] * 255);
        const hexSeismic = rgbToHex(redSEISMIC, greenSEISMIC, blueSEISMIC);
        seismicRange.push(interpolateRgb(hexSeismic)(mapColors["seismic"]["value"][index]));
      }
      //coger las medidas que tendran todos los asset y hacer un array de colores definitivo
      let result = {
        status: scaleLinear()
          .domain([0, 0.55])
          .range([interpolateRgb("green")(0), interpolateRgb("red")(0.55)]),
        "status.secondary": scaleLinear()
          .domain([0, 0.55])
          .range([interpolateRgb("green")(0), interpolateRgb("red")(0.55)]),
        leaks: scaleLinear()
          .domain([0, 0.55])
          .range([interpolateRgb("green")(0), interpolateRgb("red")(0.55)]),
        temperature: scaleLinear().domain(jetDomain).range(jetRange),
        temperature_matrix: scaleLinear().domain(jetDomain).range(jetRange),
        conductivity: scaleLinear().domain(jetDomain).range(jetRange),
        voltages: scaleLinear().domain(jetDomain).range(jetRange),
        currents: scaleLinear().domain(jetDomain).range(jetRange),
        landslide: scaleLinear()
          .domain([0, 0.55])
          .range([interpolateRgb("green")(0), interpolateRgb("red")(0.55)]),
        strain: scaleLinear().domain(seismicDomain).range(seismicRange),
      };
      return result;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapColors]);

  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.onmessage = null;
        wsRef.current.onopen = null;
        wsRef.current.onclose = null;

        wsRef.current.close();

        wsRef.current = null;
      }
    };
  }, []);

  function componentToHex(c) {
    var hex = c.toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  }

  function rgbToHex(r, g, b) {
    return "#" + componentToHex(r) + componentToHex(g) + componentToHex(b);
  }

  const [selectedType, setSelectedType] = useState(0);
  // eslint-disable-next-line no-unused-vars
  const [currentAsset, setCurrentAsset] = useState(0);
  const [filter, setFilter] = useState("status");
  const [filtered_asset, setFiltered_Asset] = useState("");
  const [currentAssetValues, setCurrentAssetValues] = useState();
  // eslint-disable-next-line no-unused-vars
  const [measureMinus, setMeasureMinus] = useState(0);

  const bounds = useMemo(() => !isNil(assets) && assets.map((item) => item.centroid), [assets]);

  const assetsOptions = useMemo(() => {
    let result = [];
    if (!isNil(assets)) {
      if (assets.length > 1) {
        result = [intl.formatMessage({ id: "all" })];
      }
      result = [...result, ...assets.map((a) => a.name)];
    }
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assets]);
  const [currentAssetMeasure, setCurrentAssetMeasure] = useState([]);

  const polylines = useMemo(() => {
    if (!isNil(assets) && !isEmpty(assetsValues?.data) && !isNil(points?.data) && !isEmpty(points?.data)) {
      let bordersClone = clone(borders);
      let auxEquipmentIndex = -1;
      const newInc = [];
      const newCrit = [];
      const newAvoid = [];
      const newProt = [];
      const newExtra = [];
      const result = assets.reduce((acc, asset, reduceIndex) => {
        let minLon = 0,
          maxLon = 0,
          minLat = 0,
          maxLat = 0;
        if (inside.includes(asset.name)) {
          for (let indexEquipment = 0; indexEquipment < asset.equipments.length; indexEquipment++) {
            auxEquipmentIndex++;
            const aValues = assetsValues.data[asset.name][asset.equipments[indexEquipment].equipmentID];
            const aValuesCopy = JSON.parse(JSON.stringify(aValues));
            const statusMeasures = []; // the first values for each measures for using them into the prevValues variable

            const equipment = asset.equipments[indexEquipment];
            if (!isEmpty(equipment.areas) && equipment.equipmentType === "DAS") {
              for (let areaIndex = 0; areaIndex < equipment.areas.main?.length; areaIndex++) {
                newCrit.push(equipment.areas.main[areaIndex].critical.geometry.coordinates);
                newProt.push(equipment.areas.main[areaIndex].protection.geometry.coordinates);
              }
              for (let indexAvoid = 0; indexAvoid < equipment.areas.avoid?.length; indexAvoid++) {
                newAvoid.push(equipment.areas.avoid[indexAvoid]);
              }
              for (let indexExtra = 0; indexExtra < equipment.areas.extra?.length; indexExtra++) {
                newExtra.push(equipment.areas.extra[indexExtra]);
              }
            }
            const equipmentType = objectMeasures.find((item) => item.name === equipment.equipmentType);
            let measures = filter === "status" ? stateMagnitudes : currentAssetMeasure;
            for (let j = 0; j < measures.length; j++) {
              const measure = measures[j];
              if (!isNil(aValues?.[measure])) statusMeasures.push(aValues[measure]?.[0]); //
            }

            const umbrales = [];

            const maxPercentiles = [];
            const minPercentiles = [];
            const equipmentAdditionalMeasures = [];
            if (isEmpty(selectedSubs[auxEquipmentIndex]) || isNil(selectedSubs[auxEquipmentIndex])) {
              for (let index = 0; index < Object.keys(aValues).length; index++) {
                const channels = equipment.channels;
                if (!isEmpty(selectedSubs)) {
                  const actualSub = selectedSubs[auxEquipmentIndex][index];
                  if (!isNil(actualSub)) {
                    let channelIndex = -1;
                    for (let i = 0; i < channels.length; i++) {
                      const subs = channels[i].substructures;
                      if (subs?.some((s) => s === actualSub.ref)) {
                        channelIndex = i;
                        break;
                      }
                    }
                    const subNumber = (parseInt(channelIndex) + 1).toString();
                    const channelData = aValues[subNumber][actualSub.ref];
                    if (!isNil(channelData) && !isEmpty(channelData)) {
                      if (Object.keys(channelData.additional_measures).length > 0) {
                        equipmentAdditionalMeasures.push(Object.keys(channelData.additional_measures));
                      }
                      if (!isEmpty(channelData) && !isNil(channelData.boundaries[filter])) {
                        maxPercentiles.push(channelData.boundaries[filter][1]);
                        minPercentiles.push(channelData.boundaries[filter][0]);
                      }
                    }
                  }
                }
              }
            } else {
              for (let index = 0; index < selectedSubs[auxEquipmentIndex].length; index++) {
                const channels = equipment.channels;
                const actualSub = selectedSubs[auxEquipmentIndex][index];

                let channelIndex = -1;

                for (let i = 0; i < channels.length; i++) {
                  const subs = channels[i].substructures;

                  if (subs?.some((s) => s === actualSub.ref)) {
                    channelIndex = i;
                    break;
                  }
                }
                if (!equipment.channels[channelIndex].state) {
                  if (
                    toastWarning &&
                    equipment.channels[channelIndex].info.desc.length > 0 &&
                    !isNil(equipment.channels[channelIndex].info.desc)
                  ) {
                    toast.warning(
                      <>
                        {intl.formatMessage({ id: "equipment" })} {equipment.name}
                        <br />
                        {intl.formatMessage({ id: "channel" })} {index + 1}:
                        {equipment.channels[channelIndex].info.desc.length > 0 && (
                          <>
                            <b>{intl.formatMessage({ id: equipment.channels[channelIndex].info.desc })}</b>
                          </>
                        )}
                      </>,
                      {
                        className: "toast-warning",
                      },
                    );
                    setToastWarning(false);
                  }
                } else {
                  const subNumber = (parseInt(channelIndex) + 1).toString();
                  const channelData = aValues[subNumber][actualSub.ref];
                  if (!isNil(channelData) && !isEmpty(channelData)) {
                    if (
                      !isNil(channelData.additional_measures) &&
                      Object.keys(channelData.additional_measures).length > 0
                    ) {
                      equipmentAdditionalMeasures.push(Object.keys(channelData.additional_measures));
                    }
                    if (!isEmpty(channelData) && !isNil(channelData.boundaries[filter])) {
                      maxPercentiles.push(channelData.boundaries[filter][1]);
                      minPercentiles.push(channelData.boundaries[filter][0]);
                    }
                  }
                  if ((isNil(channelData) || isEmpty(channelData)) && equipment.equipmentType !== "DAS") {
                    if (toastWarning) {
                      toast.warning(`${intl.formatMessage({ id: "the.channel.has.no.data" })}`, {
                        className: "toast-warning",
                      });
                      setToastWarning(false);
                    }
                  } else {
                  }
                }
              }
            }

            const max =
              Math.max(...maxPercentiles) === Infinity || Math.max(...maxPercentiles) === -Infinity
                ? 1
                : Math.max(...maxPercentiles);
            const min =
              Math.min(...minPercentiles) === -Infinity || Math.min(...minPercentiles) === Infinity
                ? 0
                : Math.min(...minPercentiles);
            if (!isNil(selectedSubs) && !isEmpty(selectedSubs) && !isNil(selectedSubs[auxEquipmentIndex])) {
              for (let indexSub = 0; indexSub < selectedSubs[auxEquipmentIndex].length; indexSub++) {
                const channels = equipment.channels;
                const actualSub = selectedSubs[auxEquipmentIndex][indexSub];
                if (actualSub.type === "inclinometer") {
                  newInc.push(actualSub);
                }
                if (actualSub.type === "generic") {
                  let indexChannel = -1;
                  for (let i = 0; i < channels.length; i++) {
                    const subs = channels[i].substructures;
                    if (subs?.some((s) => s === actualSub.ref)) {
                      indexChannel = i;
                      break;
                    }
                  }
                  const count =
                    points.data?.[`${asset.name}_${actualSub.polyline}_${equipment.equipmentType}`]?.points.length;
                  if (
                    !isNil(aValuesCopy) &&
                    !isNil(aValuesCopy[(indexChannel + 1).toString()]) &&
                    !isNil(aValuesCopy[(indexChannel + 1).toString()][actualSub.ref])
                  ) {
                    aValuesCopy[(indexChannel + 1).toString()][actualSub.ref].vibrations = [];
                  }
                  if (equipment.equipmentType === "DAS") {
                    if (isNil(aValuesCopy[(indexChannel + 1).toString()][actualSub.ref])) {
                      aValuesCopy[(indexChannel + 1).toString()][actualSub.ref] = {};
                    }
                    for (let indexCount = 0; indexCount < count; indexCount++) {
                      if (isNil(aValuesCopy[(indexChannel + 1).toString()][actualSub.ref][filter])) {
                        aValuesCopy[(indexChannel + 1).toString()][actualSub.ref][filter] = [];
                        aValuesCopy[(indexChannel + 1).toString()][actualSub.ref][filter].push(0);
                      } else {
                        aValuesCopy[(indexChannel + 1).toString()][actualSub.ref][filter].push(0);
                      }
                    }
                  }
                  let prevValues = {
                    state: null,
                    values:
                      filter === "status" || filter === "status.secondary"
                        ? statusMeasures
                        : !isNil(aValues)
                          ? !isNil(aValues[(indexChannel + 1).toString()][actualSub.ref]) &&
                            !isEmpty(aValues[(indexChannel + 1).toString()][actualSub.ref])
                            ? aValues[(indexChannel + 1).toString()][actualSub.ref][
                                currentAssetMeasure[selectedType - measureMinus]
                              ]?.[0]
                            : null
                          : null,
                  }; // has the measure values for the previous visited point
                  const positionsRepeated = [];
                  for (let i = 0; i < equipmentSubs[auxEquipmentIndex].length; i++) {
                    for (let j = 0; j < equipmentSubs[auxEquipmentIndex][i].substructures.length; j++) {
                      const actualSubIndex = equipmentSubs[auxEquipmentIndex][indexChannel].substructures.findIndex(
                        (sub) => sub === actualSub.ref,
                      );
                      const subFound1 = asset.substructures.find(
                        // eslint-disable-next-line no-loop-func
                        (sub) => sub.ref === equipmentSubs[auxEquipmentIndex][i].substructures[j],
                      );
                      const subFound2 = asset.substructures.find(
                        // eslint-disable-next-line no-loop-func
                        (sub) =>
                          sub.ref === equipmentSubs[auxEquipmentIndex][indexChannel].substructures[actualSubIndex],
                      );
                      if (actualSubIndex >= 0 && subFound1.polyline === subFound2.polyline) {
                        positionsRepeated.push(j + acc.length - indexSub);
                      }
                    }
                  }
                  const filteredPoints = [];
                  if (!isNil(actualSub)) {
                    let mult = 4;
                    for (let indexDiff = 0; indexDiff < differencesForTempLength[0].length; indexDiff++) {
                      if (count < differencesForTempLength[0][indexDiff].range) {
                        mult = differencesForTempLength[0][indexDiff].multiplier;
                      }
                    }
                    const finalAvalues = equipment.equipmentType === "DAS" ? aValuesCopy : aValues;
                    for (let i = 0; i < count; i++) {
                      let p = points.data?.[`${asset.name}_${actualSub.polyline}_${equipment.equipmentType}`].points[i];
                      if (
                        !isNil(p) &&
                        !isNil(finalAvalues[(indexChannel + 1).toString()][actualSub.ref]) &&
                        !isEmpty(finalAvalues[(indexChannel + 1).toString()][actualSub.ref])
                      ) {
                        const channelValues = finalAvalues[(indexChannel + 1).toString()][actualSub.ref];
                        const additional = !isNil(equipmentAdditionalMeasures[indexChannel])
                          ? equipmentAdditionalMeasures[indexChannel].includes(filter)
                          : null;
                        const additionalValues = additional ? channelValues.additional_measures[filter] : null;
                        const valores = [];
                        if (filter === "status" || filter === "status.secondary") {
                          for (let measuresIndex = 0; measuresIndex < measures.length; measuresIndex++) {
                            if (!equipmentType.computing.includes(measures[measuresIndex])) {
                              valores.push(channelValues[measures[measuresIndex]][i]);
                            }
                          }
                        }
                        const differenceMultiplier = filter === "temperature" ? 1 * mult : 6;
                        const shouldAdd =
                          filter === "status" || filter === "status.secondary"
                            ? shouldAddAccordingToState({
                                indexes:
                                  points.data?.[`${asset.name}_${actualSub.polyline}_${equipment.equipmentType}`]
                                    .must_graph_points,
                                index: i,
                                thresholds: umbrales,
                                values: valores,
                                prevValues,
                                filteredPoints,
                                measures,
                              })
                            : shouldAddAccordingToMeasure({
                                indexes:
                                  points.data?.[`${asset.name}_${actualSub.polyline}_${equipment.equipmentType}`]
                                    .must_graph_points,
                                index: i,
                                thresholds: umbrales,
                                difference: !isNil(channelValues.differences)
                                  ? channelValues.differences[filter] * differenceMultiplier
                                  : 0,
                                value: !isNil(channelValues[currentAssetMeasure[selectedType - measureMinus]]?.[i])
                                  ? isNil(additionalValues)
                                    ? channelValues[currentAssetMeasure[selectedType - measureMinus]][i]
                                    : null
                                  : 0,
                                prevValues,
                                filteredPoints,
                              });
                        if (shouldAdd.should) {
                          filteredPoints.push({
                            ...p,
                            index: i,
                            values:
                              filter === "status" || filter === "status.secondary"
                                ? !isNil(shouldAdd.measuresValue)
                                  ? shouldAdd.measuresValue
                                  : shouldAdd.values
                                : shouldAdd.measureValue,
                            measureIndex: selectedType - measureMinus,
                          });
                        }
                      }
                    }
                    if (!isEmpty(filteredPoints)) {
                      const pList = []; // polylines list
                      for (let i = 0; i < filteredPoints.length - 1; i++) {
                        const p1 = filteredPoints[i];
                        const p2 = filteredPoints[i + 1];
                        if (indexEquipment === 0 && indexChannel === 0 && i === 0) {
                          maxLat = p1.latitude;
                          minLat = p1.latitude;
                          maxLon = p1.longitude;
                          minLon = p1.latitude;
                        } else {
                          if (minLat > p1.latitude) {
                            minLat = p1.latitude;
                          } else if (maxLat < p1.latitude) {
                            maxLat = p1.latitude;
                          }
                          if (minLon > p1.longitude) {
                            minLon = p1.longitude;
                          } else if (maxLon < p1.longitude) {
                            maxLon = p1.longitude;
                          }
                        }

                        const color =
                          equipment.equipmentType === "DAS"
                            ? "green"
                            : getLineColor({
                                filter,
                                asset,
                                equipment,
                                data: aValues[(indexChannel + 1).toString()][actualSub.ref],
                                max,
                                min,
                                p: p1,
                                assetIndex: reduceIndex,
                                tColorScale,
                                umbrales,
                                //minBoundaries,
                                //maxBoundaries,
                              });

                        pList.push({ p1, p2, alertType: p1.alertType, color });
                      }

                      const lastPoint = filteredPoints[filteredPoints.length - 1];
                      const color =
                        equipment.equipmentType === "DAS"
                          ? "green"
                          : getLineColor({
                              filter,
                              asset,
                              equipment,
                              data: aValues[(indexChannel + 1).toString()][actualSub.ref],
                              max,
                              min,
                              p: lastPoint,
                              assetIndex: reduceIndex,
                              tColorScale,
                              umbrales,
                              //minBoundaries,
                              //maxBoundaries,
                            });
                      const selSub = [];
                      for (let indexSelSub = 0; indexSelSub < selectedSubs[auxEquipmentIndex].length; indexSelSub++) {
                        selSub.push(selectedSubs[auxEquipmentIndex][indexSelSub].polyline + acc.length - indexSub);
                      }
                      pList.push({
                        p1: lastPoint,
                        p2: lastPoint,
                        alertType: lastPoint.alertType,
                        color,
                        equal: positionsRepeated,
                        asset: asset.name,
                        selectedSubs: selSub,
                        equipmentIndex: auxEquipmentIndex,
                        actualSub: actualSub,
                        id_polyline: points.data?.[`${asset.name}_${actualSub.polyline}_${equipment.equipmentType}`].id,
                      });
                      acc.push(pList);
                    }
                  }
                }
              }
            }
          }
          const deltaLat = (maxLat - minLat) / 100;
          const deltaLon = (maxLon - minLon) / 100;
          bordersClone[asset.name] = [
            [minLat, minLon],
            [maxLat, maxLon],
            [(minLat + maxLat) / 2, (minLon + maxLon) / 2],
          ];
          for (let borderIndex = 0; borderIndex < 100; borderIndex++) {
            bordersClone[asset.name].push([minLat + borderIndex * deltaLat, minLon + borderIndex * deltaLon]);
          }
          setLoading(false);
          if (mapRef.current) {
            mapRef.current.dragging.enable();
            mapRef.current.doubleClickZoom.enable();
            mapRef.current.touchZoom.enable();
            mapRef.current.boxZoom.enable();
            mapRef.current.keyboard.enable();
          }
          return acc;
        } else {
          return acc;
        }
      }, []);
      setBorders(bordersClone);
      setInclinometerList(newInc);
      setPolygonCritical(newCrit);
      setPolygonAvoid(newAvoid);
      setPolygonProtect(newProt);
      setPolygonExtra(newExtra);

      return result;
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assets, assetsValues, points, filter, equipmentSubs, selectedSubs, visible]);

  const { alerts: computedAssetsAlerts, names: computedAssetsAlertsFilter } = useMemo(() => {
    return computeAlerts({
      inside,
      assetsValues,
      alerts,
    });
  }, [inside, assetsValues, alerts]);
  useEffect(() => {
    if (!computedAssetsAlertsFilter && isEmpty(computedAssetsAlertsFilter)) return;

    setSelectedAssetsAlertsFilter((prevSelected) => {
      // PRIMERA VEZ → seleccionar todo
      if (!isEmpty(inside) && !isInitialized) {
        setIsInitialized(true);
        return computedAssetsAlertsFilter;
      }

      // mantener solo los que siguen existiendo
      return prevSelected.filter((opt) => computedAssetsAlertsFilter.includes(opt));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computedAssetsAlertsFilter, isInitialized]);

  const [maxBounds, setMaxBounds] = useState(null);
  const [originalBounds, setOriginalBounds] = useState(null);
  const zoom = useMemo(() => {
    let result = 8;
    if (!isNil(assets)) {
      if (assets.length === 1) {
        result = assets[0]?.zoom;
      } else if (!isNil(bounds)) {
        result = 5;
      }
    }
    return result;
  }, [assets, bounds]);

  const [mapCentroid, setMapCentroid] = useState();

  const [mapType, setMapType] = useState("regular");
  const [currentZoom, setCurrentZoom] = useState(zoom);
  const [assetZoom, setAssetZoom] = useState(zoom);

  const [reset, setReset] = useState(true);
  useEffect(() => {
    let effectZoom = assets.length === 1 ? assets[0]?.zoom - 2 : 8;
    if (mapRef.current && assetZoom <= effectZoom) {
      const currentBounds = mapRef.current.getBounds();
      const southWest = currentBounds.getSouthWest();
      const northEast = currentBounds.getNorthEast();

      if (!bounds.length) return;

      const lats = bounds.map(([lat]) => lat);
      const lngs = bounds.map(([, lng]) => lng);

      const margin = 0.5;

      const minLat = Math.min(Math.min(...lats) - margin, southWest.lat);
      const maxLat = Math.max(Math.max(...lats) + margin, northEast.lat);
      const minLng = Math.min(Math.min(...lngs) - margin, southWest.lng);
      const maxLng = Math.max(Math.max(...lngs) + margin, northEast.lng);

      const newBounds = [
        [minLat, minLng],
        [maxLat, maxLng],
      ];

      setMaxBounds(newBounds);
      setOriginalBounds(newBounds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef.current]);

  useEffect(() => {
    let result = null;
    if (!isNil(assets)) {
      result = getCentroid(assets.map((item) => item.centroid));
    }
    setMapCentroid(result);
  }, [assets]);

  // Componente dedicado SOLO a los eventos del mapa
  function MapEvents({ onZoomEnd, onDragEnd }) {
    const mapView = useMap();

    useMapEvents({
      zoomend: (event) => {
        if (programmaticMoveRef.current) {
          programmaticMoveRef.current = false; // consumimos el flag, ignoramos este evento
          return;
        }
        onZoomEnd(event, mapView);
      },
      dragend: (event) => {
        if (programmaticMoveRef.current) {
          programmaticMoveRef.current = false;
          return;
        }
        onDragEnd(event, mapView);
      },
    });

    return null;
  }

  const programmaticMoveRef = useRef(false);

  // ChangeView solo gestiona la vista
  function ChangeView({ mapCentroid, reset, selectedEquipment, currentZoom, maxBounds, setReset }) {
    const mapView = useMap();

    useEffect(() => {
      if (!isNil(mapCentroid) && reset && isNil(selectedEquipment)) {
        mapView.setMaxBounds(null);
        programmaticMoveRef.current = true;
        setTimeout(() => {
          mapView.setView(mapCentroid, currentZoom, { animate: false });
          setReset(false);
        }, 0);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapCentroid, reset, selectedEquipment, currentZoom, mapView]);

    useEffect(() => {
      if (maxBounds && !reset) {
        mapView.setMaxBounds(maxBounds);
      }
    }, [maxBounds, reset, mapView]);

    return null;
  }
  function puntoDentroRectangulo(x1, y1, x2, y2, xc, yc) {
    if (xc === 0 && yc === 0) {
      return false;
    }
    if (xc >= x1 && xc <= x2 && yc >= y1 && yc <= y2) {
      return true;
    } else {
      return false;
    }
  }

  async function assetDataReceiving(event, mapView) {
    const pointsZoom = {};
    const assetsValuesZoom = {};
    const insideZoom = [];
    const visibleZoom = [];
    const equipmentsInside = [];
    const channelsInside = [];
    const subsInside = [];
    const channelNamesInside = [];
    let selectedChannelsZoom = [];
    let selectedSubsZoom = [];
    const currentMeasuresIN = [];
    let isFirstTime = true;
    const detailFlags = {};
    const targetZoom = event.target.getZoom();
    setCurrentZoom(targetZoom);
    let auxIndexEquipment = -1;
    const isDentroMap = {};
    let anyQualifies = false;
    const bounds = mapView.getBounds();
    for (let index = 0; index < assets.length; index++) {
      const name = assets[index].name;
      const centroid = [assets[index].centroid[0], assets[index].centroid[1]];
      const centro = { lat: centroid[0], lng: centroid[1] };
      let estaEnLinea = false;
      for (let borderIndex = 0; borderIndex < 103; borderIndex++) {
        if (!isNil(borders[name][borderIndex])) {
          if (bounds.contains({ lat: borders[name][borderIndex][0], lng: borders[name][borderIndex][1] })) {
            estaEnLinea = true;
          }
        }
      }
      if (inside.includes(name) && !isNil(borders[name][0])) {
        estaEnLinea = puntoDentroRectangulo(
          borders[name][0][0],
          borders[name][0][1],
          borders[name][1][0],
          borders[name][1][1],
          borders[name][2][0],
          borders[name][2][1],
        );
      }
      const isDentro = bounds.contains(centro) || estaEnLinea;
      isDentroMap[name] = isDentro;

      if (isDentro && targetZoom >= assets[index].zoom) {
        anyQualifies = true;
      }
    }
    for (let index = 0; index < assets.length; index++) {
      const name = assets[index].name;
      const isDentro = isDentroMap[name];
      if (isDentro && targetZoom === 10) {
        const southWest = bounds.getSouthWest(); // Devuelve un LatLng
        const northEast = bounds.getNorthEast(); // Devuelve un LatLng
        setMaxBounds([
          [southWest.lat, southWest.lng],
          [northEast.lat, northEast.lng],
        ]);
        setMaxBounds(originalBounds);
        setPoints({ data: [] });
        setAssetsValues({ data: [] });
        setInside([]);
        setVisible([]);
        setAssetEquipments([]);
        setSelectedEquipment(null);
        setIndexEquipmentChannel(0);
        setEquipmentSubs([]);
        setEquipmentChannelNames([]);
        setSelectedChannels([]);
        setSelectedSubs([]);
        setSubList([]);
        setToastWarning(true);
        setCurrentAsset(0);
        setFiltered_Asset("");
        setPolygonCritical(null);
        setPolygonAvoid(null);
        setPolygonProtect(null);
        setIsInitialized(false);
        setSelectedAssetsAlertsFilter([]);
        setShipHistory(null);
        setShips([]);
        setSelectedShip(null);
        setShipsStatus();
        setShipsTracking();
        setHidePolygons(false);
        if (wsRef.current) {
          wsRef.current.onmessage = null;
          wsRef.current.onopen = null;
          wsRef.current.onclose = null;

          wsRef.current.close();

          wsRef.current = null;
        }
        detailFlags[name] = false;
        filterInitializedRef.current = false;
        setDetailViewMap({});
      } else if (targetZoom < 10) {
        //setReset(false);
        setMaxBounds(originalBounds);
        setPoints({ data: [] });
        setAssetsValues({ data: [] });
        setInside([]);
        setVisible([]);
        setAssetEquipments([]);
        setSelectedEquipment(null);
        setIndexEquipmentChannel(0);
        setEquipmentSubs([]);
        setEquipmentChannelNames([]);
        setSelectedChannels([]);
        setSelectedSubs([]);
        setSubList([]);
        setToastWarning(true);
        setIsInitialized(false);
        setFiltered_Asset("");
        setSelectedShip(null);
        setCurrentAsset(0);
        setPolygonCritical(null);
        setPolygonAvoid(null);
        setPolygonProtect(null);
        setSelectedAssetsAlertsFilter([]);
        setShipHistory(null);
        setShips([]);
        setShipsStatus();
        setShipsTracking();
        setHidePolygons(false);
        if (wsRef.current) {
          wsRef.current.onmessage = null;
          wsRef.current.onopen = null;
          wsRef.current.onclose = null;

          wsRef.current.close();

          wsRef.current = null;
        }
        setDetailViewMap({});
        filterInitializedRef.current = false;
      }
      if (isDentro && anyQualifies) {
        detailFlags[name] = true;
        setMapCentroid(assets[index].centroid);
        insideZoom.push(name);
        if (inside.includes(name)) {
          const indexVisible = inside.indexOf(name);
          for (let indexEquipment = 0; indexEquipment < assets[index].equipments.length; indexEquipment++) {
            visibleZoom.push(visible[indexVisible]);
          }
          setToastWarning(false);
        } else {
          for (let indexEquipment = 0; indexEquipment < assets[index].equipments.length; indexEquipment++) {
            visibleZoom.push(true);
          }
          setToastWarning(true);
        }
        equipmentsInside.push(assets[index].equipments);
        const newMeasurands = ["status"];
        for (let indexMeasurand = 0; indexMeasurand < assets[index]?.config?.modules?.length; indexMeasurand++) {
          const actualModule = assets[index].config.modules[indexMeasurand];
          const auxPack = actualModule.packages.filter((pack) => pack.measurand);
          const newPackages = auxPack.map((pack) => pack.name);
          newMeasurands.push(...newPackages);
        }
        currentMeasuresIN.push(newMeasurands);
        for (let indexEquipment = 0; indexEquipment < assets[index].equipments.length; indexEquipment++) {
          auxIndexEquipment++;
          channelsInside.push(assets[index].equipments[indexEquipment].channels);
          subsInside.push([]);
          //channelNamesInside.push([]);
          for (
            let indexChannel = 0;
            indexChannel < assets[index].equipments[indexEquipment].channels.length;
            indexChannel++
          ) {
            const channelSubs = assets[index].equipments[indexEquipment].channels[indexChannel].substructures;

            const subsFound = assets[index].substructures.filter((sub) => channelSubs.includes(sub.ref));
            subsInside[auxIndexEquipment].push(...subsFound);
            channelNamesInside.push(assets[index].equipments[indexEquipment].channels[indexChannel].name);
          }
          const auxChan = [];
          const auxSub = [];
          for (let indexAux = 0; indexAux < assets[index].equipments[indexEquipment].channels.length; indexAux++) {
            const channelSubs = assets[index].equipments[indexEquipment].channels[indexAux].substructures;

            const subsFound = assets[index].substructures.filter((sub) => channelSubs.includes(sub.ref));
            auxChan.push(indexAux);
            auxSub.push(...subsFound);
          }
          selectedChannelsZoom.push(auxChan);
          selectedSubsZoom.push(auxSub);
        }
        if (isNil(assetsValues.data[name])) {
          if (isFirstTime) {
            //setFilter(assets[index].equipments[0].pMeasureNames[0]);
            //setCurrentAsset(index + 1);
            if (isNil(filtered_asset) || filtered_asset === "") {
              setFiltered_Asset(assets[index].name);
              setAssetZoom(assets[index].zoom);
            }
            isFirstTime = false;
          }
          //setReset(true);
          setLoading(true);
          mapView.dragging.disable();
          mapView.doubleClickZoom.disable();
          mapView.touchZoom.disable();
          mapView.boxZoom.disable();
          mapView.keyboard.disable();
          const response = await getPointsByAssets(name);
          for (let index = 0; index < response.data.data.length; index++) {
            pointsZoom[`${name}_${response.data.data[index].id}_${response.data.data[index].equipment_type}`] =
              response.data.data[index];
          }

          dispatch(setAssetsPointsData(response.data));
          const response2 = await getAssetsValuesByAssets(name);
          assetsValuesZoom[name] = response2.data.data;
          setCurrentAssetValues(response2.data.data);
          dispatch(setAssetsData(response2.data));
        } else {
          let cont = 0;
          let realIndex = [];
          const allEquipments = [];
          for (let indexAsset = 0; indexAsset < assetEquipments.length; indexAsset++) {
            for (let indexAssetEquip = 0; indexAssetEquip < assetEquipments[indexAsset].length; indexAssetEquip++) {
              allEquipments.push(assetEquipments[indexAsset][indexAssetEquip]);
              if (assetEquipments[indexAsset][indexAssetEquip].asset === name) {
                realIndex.push(cont);
              }
              cont++;
            }
          }
          cont = 0;
          for (let indexInside = 0; indexInside < equipmentsInside.length; indexInside++) {
            if (equipmentsInside.length - 1 > indexInside) {
              cont += equipmentsInside[indexInside].length; //para saber cuantos equipos hay de antes
            }
          }
          for (let indexAsset = 0; indexAsset < realIndex.length; indexAsset++) {
            selectedChannelsZoom[cont + indexAsset] = selectedChannels[realIndex[indexAsset]];
          }
          for (let indexAsset = 0; indexAsset < realIndex.length; indexAsset++) {
            selectedSubsZoom[cont + indexAsset] = selectedSubs[realIndex[indexAsset]];
          }
          const keysWithAlreadyExistingAsset = Object.keys(points.data).filter((key) => key.startsWith(name));
          // eslint-disable-next-line no-unused-vars
          const objetoConAlreadyExistingAsset = keysWithAlreadyExistingAsset.reduce((obj, key) => {
            obj[key] = points.data[key];
            pointsZoom[key] = points.data[key];
            return obj;
          }, {});
          assetsValuesZoom[name] = assetsValues.data[name];
        }
      } else if (isDentro && !anyQualifies) {
        detailFlags[name] = false;
      }
    }
    setDetailViewMap(detailFlags);
    if (!wsRef.current && !isEmpty(insideZoom)) {
      const ws = createWebSocket(accessToken, "ais", insideZoom);
      wsRef.current = ws;
      ws.onopen = (event) => {
        onOpenWS(event, "ais");
        if (insideZoom) {
          try {
            ws.send(
              JSON.stringify({
                reason: "get_ships_by_assets",
              }),
            );
          } catch (err) {
            console.error(`Error sending data on topic ships:`, err);
          }
        }
      };
      ws.onmessage = (event) => onMessageWS(event, "ais", loadData);
    }

    if (!isEmpty(currentMeasuresIN)) {
      const currentMeasuresOUT = currentMeasuresIN.flat();
      //const measuresForStatus = [];
      //if (equipmentsInside[0]?.[0].pMeasureAlerts.includes(1)) {
      //  const count = equipmentsInside[0]?.[0].pMeasureAlerts.filter((alert) => alert === 1).length;
      //  const notStatus = equipmentsInside[0]?.[0].pMeasureNames.includes("depth_of_burial") && count === 1;
      //  if (!notStatus && count > 1) {
      //    setMeasureMinus(1);
      //    currentMeasuresOUT.push("status");
      //    for (let indexStatus = 0; indexStatus < equipmentsInside[0]?.[0].pMeasureAlerts.length; indexStatus++) {
      //      if (
      //        equipmentsInside[0]?.[0].pMeasureAlerts[indexStatus] === 1 &&
      //        equipmentsInside[0]?.[0].pMeasureNames[indexStatus] !== "depth_of_burial"
      //      ) {
      //        measuresForStatus.push(equipmentsInside[0]?.[0].pMeasureNames[indexStatus]);
      //      }
      //    }
      //  } else {
      //    setMeasureMinus(0);
      //  }
      //
      //  setStateMagnitude(measuresForStatus);
      //} else {
      //  setMeasureMinus(0);
      //  setStateMagnitude([]);
      //}
      //const currentMeasuresOUT = ["status", "status.secondary"];
      for (var i = 0; i < currentMeasuresIN.length; i++) {
        var subArray = currentMeasuresIN[i];
        for (var j = 0; j < subArray.length; j++) {
          var elemento = subArray[j];
          if (currentMeasuresOUT.indexOf(elemento) === -1) {
            currentMeasuresOUT.push(elemento);
          }
        }
      }
      setCurrentAssetMeasure([...new Set(currentMeasuresOUT)]);
      if (currentMeasuresOUT.length > 0) {
        // const aux = isNil(filtered_asset) || filtered_asset === "" ? currentMeasuresOUT[0] : filter;
        if (!isNil(scales) && !filterInitializedRef.current) {
          filterInitializedRef.current = true;
          setFilter(currentMeasuresOUT[0]);
          setSelectedType(0);
          const newScale = !isNil(scales[currentMeasuresOUT[0]]) ? scales[currentMeasuresOUT[0]] : scales["status"];
          setRange(newScale.range());
          setDomain(newScale.domain());
        }
      }
    }
    if (isFirstTime) setFiltered_Asset(insideZoom[0]);
    setCurrentAssetValues(assetsValuesZoom[assets[0].name]);
    setInside(insideZoom);
    setVisible(visibleZoom);
    let foundItem = null;

    foundItem = equipmentsInside?.[0]?.find((item) => item.equipmentID === selectedEquipment);

    if (isNil(foundItem) && isNil(selectedEquipment)) {
      setSelectedEquipment(equipmentsInside[0]?.[0].equipmentID);
    } else if (isNil(foundItem) && !isNil(selectedEquipment)) {
      selectEquipmentInAssetReceiving(
        selectedEquipment,
        equipmentsInside,
        setFiltered_Asset,
        setMeasureMinus,
        setStateMagnitude,
        setSelectedEquipment,
        setIndexEquipmentChannel,
        setCurrentAsset,
        autocompleteRef,
      );
    }
    setSelectedChannels(selectedChannelsZoom);
    setSelectedSubs(selectedSubsZoom);
    //setIndexEquipmentChannel(0);

    if (equipmentsInside !== assetEquipments) setAssetEquipments(equipmentsInside);
    if (channelsInside !== equipmentSubs) setEquipmentSubs(channelsInside);
    if (subsInside !== subList) setSubList(subsInside);
    if (channelNamesInside !== equipmentChannelNames) setEquipmentChannelNames(channelNamesInside);
    setPoints((prevPoints) => ({ ...prevPoints, data: pointsZoom }));
    setAssetsValues((prevValues) => ({ ...prevValues, data: assetsValuesZoom }));
  }
  //function generateRandomString(length) {
  //  let result = "";
  //  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  //  const charactersLength = characters.length;
  //  for (let i = 0; i < length; i++) {
  //    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  //  }
  //  return result;
  //}
  function obtenerIndiceMaximo(alarmCount) {
    if (alarmCount.length === 0) {
      return -1;
    }

    let indiceMaximo = 0;
    let valorMaximo = alarmCount[0];

    for (let i = 1; i < alarmCount.length; i++) {
      if (alarmCount[i] > valorMaximo) {
        valorMaximo = alarmCount[i];
        indiceMaximo = i;
      }
    }

    return indiceMaximo;
  }
  const handlePopupOpen = (event, asset, alarmCount) => {
    const map = event.target._map;
    const popup = event.target.getPopup();
    const bounds = map.getBounds();
    const markerLatLng = event.target.getLatLng();
    const maxLat =
      !isNil(maxBounds) && bounds._northEast.lat > maxBounds[1][0] ? maxBounds[1][0] : bounds._northEast.lat;
    const minLat =
      !isNil(maxBounds) && bounds._southWest.lat > maxBounds[0][0] ? maxBounds[0][0] : bounds._southWest.lat;

    const topEdgeDistance = maxLat - markerLatLng.lat;
    const bottomEdgeDistance = minLat - markerLatLng.lat;
    const gt0 = alarmCount.some((num) => num > 0);
    if (asset.image.length > 0) {
      const reference = gt0 ? currentZoom * 1 : 0.65 * currentZoom;
      if (topEdgeDistance > reference + 0.15 || currentZoom < 5 || currentZoom > 9 || bottomEdgeDistance < 0) {
        popup.options.offset = L.point(0, -30);
        popup._container.classList.add("top-edge");
      } else {
        popup.options.offset = L.point(0, gt0 ? 440 : 290);
        popup._container.classList.remove("top-edge");
      }
    } else {
      const reference = gt0 ? 1 : 0.5 * currentZoom;

      if (topEdgeDistance > reference || currentZoom < 5 || currentZoom > 9 || bottomEdgeDistance < 0) {
        popup.options.offset = L.point(0, -30);
        popup._container.classList.add("top-edge");
      } else {
        popup.options.offset = L.point(0, gt0 ? 290 : 130);
        popup._container.classList.remove("top-edge");
      }
    }

    popup.update();
  };

  const [ships, setShips] = useState([]);
  const [shipsTracking, setShipsTracking] = useState();
  const [shipsStatus, setShipsStatus] = useState();
  const loadData = (event, topic) => {
    const result = JSON.parse(event.data);
    if (topic === "ais") {
      if (!isNil(result) && result.reason === "ships_list") {
        if (!isNil(result.data) && !isNil(result.data.ships) && !isEmpty(result.data.ships)) {
          setShips((prevShips) => {
            const shipObj = {};

            // viejos
            prevShips.forEach((ship) => {
              shipObj[ship.shipId] = ship;
            });

            // nuevos (pisan a los viejos)
            result.data.ships.forEach((ship) => {
              shipObj[ship.shipId] = ship;
            });

            return Object.values(shipObj);
          });

          //    //toast.info(intl.formatMessage({ id: "alert.list.modified" }), { className: "toast-info" });
        } else {
          setShips([]);
        }
      }
      if (!isNil(result) && result.reason === "tracking") {
        if (!isNil(result.data) && !isNil(result.data.ships) && !isEmpty(result.data.ships)) {
          setShipsTracking(result.data.ships);
          //    //toast.info(intl.formatMessage({ id: "alert.list.modified" }), { className: "toast-info" });
        } else {
          setShipsTracking([]);
        }
      }
      if (!isNil(result) && result.reason === "ships_status") {
        if (
          !isNil(result.data) &&
          !isNil(result.data.active) &&
          !isNil(result.data.inactive) &&
          (!isEmpty(result.data.active) || !isEmpty(result.data.inactive))
        ) {
          setShipsStatus((prevStatus = { inactive: [], active: {} }) => {
            return {
              inactive: Array.from(new Set([...(prevStatus.inactive || []), ...(result.data.inactive || [])])),
              active: {
                ...(prevStatus.active || {}),
                ...(result.data.active || {}),
              },
            };
          });

          //    //toast.info(intl.formatMessage({ id: "alert.list.modified" }), { className: "toast-info" });
        } else {
          setShipsStatus([]);
        }
      }
    }
  };

  useEffect(() => {
    if (!shipsStatus || isEmpty(shipsStatus)) return;
    const newShips = ships.filter((ship) => !shipsStatus.inactive.includes(ship.shipId));
    setShips(newShips);
    // eslint-disable-next-line no-use-before-define, react-hooks/exhaustive-deps
  }, [shipsStatus]);

  const shipTypes = [
    "cargo",
    "fishing",
    "high-speed-craft",
    "navigation",
    "passenger",
    "pleasure",
    "tanker",
    "tug-special",
    "unspecified",
  ];
  const headings = {
    N: 0,
    NNE: 22.5,
    NE: 45,
    ENE: 67.5,
    E: 90,
    ESE: 112.5,
    SE: 135,
    SSE: 157.5,
    S: 180,
    SSO: 202.5,
    SO: 225,
    OSO: 247.5,
    O: 270,
    ONO: 292.5,
    NO: 315,
    NNO: 337.5,
  };

  const createShipIcon = (type, heading, extra, bearing = 0) =>
    L.divIcon({
      className: "",
      html: `<div style="position: relative; width: 30px; height: 30px;">
    <img src="${require(`../../../../../images/ship-${type}.png`)}" 
      style="transform: rotate(${heading + bearing}deg); width: 30px; height: 30px;" />
    ${
      extra !== ""
        ? `<img src="${require(`../../../../../images/${extra}.png`)}" 
      style="position: absolute; top: 5px; right: 7px; width: 12px; height: 12px; z-index: 10;" />`
        : ""
    }
  </div>`,
      iconSize: [30, 30],
    });

  useEffect(() => {
    if (openPopupShipId && markerRefs.current[openPopupShipId]) {
      markerRefs.current[openPopupShipId].openPopup();
    }
  }, [ships, openPopupShipId]);
  function FixCustomPanes() {
    const map = useMap();

    useEffect(() => {
      const rotatePane = map.getPane("rotatePane") ?? document.querySelector(".leaflet-rotate-pane");

      if (!rotatePane) return;

      const panesInRotateShips = ["routePane", "shadowLightPane", "polygonsPane", "linesPane", "shipsPane"];
      panesInRotateShips.forEach((paneName) => {
        const pane = map.getPane(paneName);
        if (pane && pane.parentElement !== rotatePane) {
          rotatePane.appendChild(pane);
        }
      });

      const noRotatePane = document.querySelector(".leaflet-norotate-pane");
      if (noRotatePane) {
        const markersPane = map.getPane("markersPane");
        if (markersPane && markersPane.parentElement !== noRotatePane) {
          noRotatePane.appendChild(markersPane);
        }
      }
    }, [map]);

    return null;
  }
  useEffect(() => {
    const container = mapRef.current?.getContainer();
    if (!container) return;

    const blockWheel = (e) => {
      if (loading) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    };

    container.addEventListener("wheel", blockWheel, { capture: true, passive: false });

    return () => {
      container.removeEventListener("wheel", blockWheel, { capture: true });
    };
  }, [loading]);
  function RotateButton({ intl, setBearing, bearing }) {
    const map = useMap();

    return (
      <>
        {bearing !== 0 && (
          <IconButton
            variant="contained"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              map.setBearing(0);
              setBearing(0); // estado en el padre
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            sx={{
              backgroundColor: palette.primary,
              color: "white",
              borderRadius: "50%",
              boxShadow: "0 3px 6px rgba(0,0,0,0.3)",
              "&:hover": {
                backgroundColor: palette.primary,
                filter: "brightness(0.9)",
              },
              "&:active": {
                boxShadow: "0 1px 3px rgba(0,0,0,0.4)",
                transform: "scale(0.95)",
              },
            }}
            style={{
              position: "absolute",
              bottom: "1.4rem",
              right: "5.5rem",
              zIndex: 1000,
            }}
          >
            <ExploreOffIcon />
          </IconButton>
        )}

        <IconButton
          variant="contained"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            const newBearing = map.getBearing() + 30;
            map.setBearing(newBearing);
            setBearing(newBearing); // estado en el padre
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          sx={{
            backgroundColor: palette.primary,
            color: "white",
            borderRadius: "50%",
            boxShadow: "0 3px 6px rgba(0,0,0,0.3)",
            "&:hover": {
              backgroundColor: palette.primary,
              filter: "brightness(0.9)",
            },
            "&:active": {
              boxShadow: "0 1px 3px rgba(0,0,0,0.4)",
              transform: "scale(0.95)",
            },
          }}
          style={{
            position: "absolute",
            bottom: "1.4rem",
            right: "0.7rem",
            zIndex: 1000,
          }}
        >
          <RotateRightIcon />
        </IconButton>
        <IconButton
          variant="contained"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            const newBearing = map.getBearing() - 30;
            map.setBearing(newBearing);
            setBearing(newBearing); // estado en el padre
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          sx={{
            backgroundColor: palette.primary,
            color: "white",
            borderRadius: "50%",
            boxShadow: "0 3px 6px rgba(0,0,0,0.3)",
            "&:hover": {
              backgroundColor: palette.primary,
              filter: "brightness(0.9)",
            },
            "&:active": {
              boxShadow: "0 1px 3px rgba(0,0,0,0.4)",
              transform: "scale(0.95)",
            },
          }}
          style={{
            position: "absolute",
            bottom: "1.4rem",
            right: "3.1rem",
            zIndex: 1000,
          }}
        >
          <RotateLeftIcon />
        </IconButton>
      </>
    );
  }
  useEffect(() => {
    if (selectedShip && shipHistory) {
      setShipHistory((prev) => {
        if (!prev || prev.length === 0) return prev;
        const ship = ships.find((s) => s.shipId === selectedShip);
        const newHistory = clone(prev);
        const length = newHistory[0].polylines[0].coords.length;
        const lastCoord = newHistory[0].polylines[0].coords[length - 1];
        const shipCoord = [ship.location[1], ship.location[0]];
        if (lastCoord[0] !== shipCoord[0] || lastCoord[1] !== shipCoord[1])
          newHistory[0].polylines[0].coords.push([[ship.location[1], ship.location[0]]]);
        if (newHistory[0].polylines[0].speeds[length - 1] !== ship.speed)
          newHistory[0].polylines[0].speeds.push(ship.speed);

        return newHistory;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ships]);

  return (
    mapCentroid && (
      <MapContainer
        id="map-id"
        style={{ width: "100%" }}
        center={mapCentroid}
        bounds={bounds}
        maxBounds={maxBounds}
        ref={mapRef}
        rotate={true}
        maxBoundsViscosity={1}
        rotateControl={false}
      >
        <TileLayer
          attribution={window.RUNTIME_CONFIG.IS_ONLINE ? "Google Maps" : "OpenStreetMap"}
          url={window.RUNTIME_CONFIG.MAP_TYPES.find((type) => type.value === mapType).tileLayer}
          maxZoom={18}
          bounds={maxBounds}
          subdomains={["mt0", "mt1", "mt2", "mt3"]}
        />
        <RotateButton intl={intl} setBearing={setBearing} bearing={bearing} />
        <FixCustomPanes />
        <ChangeView
          mapCentroid={mapCentroid}
          reset={reset}
          selectedEquipment={selectedEquipment}
          currentZoom={currentZoom}
          maxBounds={maxBounds}
          setReset={setReset}
        />
        <MapEvents onZoomEnd={assetDataReceiving} onDragEnd={assetDataReceiving} />
        <Pane name="routePane" style={{ zIndex: 300 }} />
        <Pane name="shadowLightPane" style={{ zIndex: 400 }} />
        <Pane name="polygonsPane" style={{ zIndex: 400 }} />
        <Pane name="linesPane" style={{ zIndex: 500 }} />
        <Pane name="markersPane" style={{ zIndex: 600 }} />
        {!isNil(currentZoom) &&
          !isNil(assets) &&
          assets.map((asset, assetIndex) => {
            const showDetail = detailViewMap[asset.name];

            if (showDetail) {
              if (
                !(filter === "status" || filter === "status.secondary" || inside.includes(asset.name)) ||
                isNil(polylines?.[inside.indexOf(asset.name)])
              ) {
                return null;
              }
              const aux = [];
              return (
                <div key={`${asset.name}-${assetIndex}`}>
                  {polylines.map((polyline, plineIndex) => {
                    const insideIndex = inside.indexOf(asset.name);
                    const equipmentIndex = polyline[polyline.length - 1].equipmentIndex;
                    if (visible[equipmentIndex] && polyline[polyline.length - 1].asset === asset.name) {
                      let count = 0;
                      for (let i = 0; i < insideIndex; i++) {
                        count += assetEquipments[i]?.length || 0;
                      }
                      const equipmentData = assetEquipments[insideIndex][equipmentIndex - count];
                      if (
                        equipmentData.equipmentType === "DAS" &&
                        filter !== "status" &&
                        aux.includes(polyline[polyline.length - 1].id_polyline)
                      )
                        return null;
                      const asociado = asociarNumeroConLista(currentZoom, window.RUNTIME_CONFIG.lista);
                      const repeated = polyline[polyline.length - 1].equal;
                      const realIndex = plineIndex - Math.min(...repeated);
                      const selSubs = polyline[polyline.length - 1].selectedSubs;
                      const length = repeated.length;

                      aux.push(polyline[polyline.length - 1].id_polyline);
                      const indexSubPol = aux.filter((x) => x === polyline[polyline.length - 1].id_polyline).length;

                      let alertsInPolyline = [];

                      if (indexSubPol > 1 && filter !== "status") {
                        const m = indexSubPol % 2 === 0 ? 1 : -1;
                        const extra =
                          indexSubPol % 2 === 0 ? Math.round((realIndex + 1) / 2) : Math.round(realIndex / 2);
                        const distance = asociado * m * extra;
                        const pointIni = [polyline[0].p1.latitude, polyline[0].p1.longitude];
                        const pointEnd = [
                          polyline[polyline.length - 1].p2.latitude,
                          polyline[polyline.length - 1].p2.longitude,
                        ];
                        const atanV1 = Math.atan2(pointEnd[0] - pointIni[0], pointEnd[1] - pointIni[1]);
                        const offset_v1_latitude = -distance * Math.cos(atanV1);
                        const offset_v1_longitude = distance * Math.sin(atanV1);
                        return polyline.map((line, lineIndex) => {
                          const linep1 = line.p1.index;
                          const linep2 = line.p2.index;
                          const isAlert = alertsInPolyline.some(
                            (alert) => alert.startIndex <= linep1 && linep2 <= alert.endIndex,
                          );
                          const lineColor = isAlert ? "red" : line.color;
                          return (
                            <Polyline
                              key={`${filter}-${asset.name}-${lineIndex}-${plineIndex}-zoom${currentZoom}-${equipmentData.equipmentType}-offset`}
                              positions={[
                                [line.p1.latitude + offset_v1_latitude, line.p1.longitude + offset_v1_longitude],
                                [line.p2.latitude + offset_v1_latitude, line.p2.longitude + offset_v1_longitude],
                              ]}
                              weight={
                                currentZoom === 11 || currentZoom === 12
                                  ? 8
                                  : currentZoom === 13 || currentZoom === 14
                                    ? 6.5
                                    : 5
                              }
                              color={lineColor}
                              pane="linesPane"
                            ></Polyline>
                          );
                        });
                      } else {
                        return polyline.map((line, lineIndex) => {
                          const linep1 = line.p1.index;
                          const linep2 = line.p2.index;
                          const isAlert = alertsInPolyline.some(
                            (alert) => alert.startIndex <= linep1 && linep2 <= alert.endIndex,
                          );
                          const lineColor = isAlert ? "red" : line.color;
                          return (
                            <Polyline
                              key={`${filter}-${asset.name}-${lineIndex}-${plineIndex}-zoom${currentZoom}-${equipmentData.equipmentType}`}
                              positions={[
                                [line.p1.latitude, line.p1.longitude],
                                [line.p2.latitude, line.p2.longitude],
                              ]}
                              weight={
                                currentZoom === 11 || currentZoom === 12
                                  ? 8
                                  : currentZoom === 13 || currentZoom === 14
                                    ? 6.5
                                    : 5
                              }
                              color={lineColor}
                              pane="linesPane"
                            ></Polyline>
                          );
                        });
                      }
                    }
                    return null;
                  })}
                </div>
              );
            }

            // --- Modo marker para ESTE asset concreto ---
            const alarmCountM = alarmsQuantity(
              alerts?.measurements.filter((a) => a.asset === asset.name && !a.isArchived),
            );
            const alarmCountE = alarmsQuantity(
              alerts?.equipments.filter((a) => a.asset === asset.name && !a.isArchived),
            );
            const alarmCount = [
              alarmCountM[0] + alarmCountE[0],
              alarmCountM[1] + alarmCountE[1],
              alarmCountM[2] + alarmCountE[2],
            ];
            const equipments = asset.equipments;
            const notAlive = equipments.some((objeto) => !objeto.statusInfo.alive);
            const noAlerts = alarmCount.every((valor) => valor === 0);
            const indice = noAlerts ? 0 : obtenerIndiceMaximo(alarmCount) + 1;
            const markerIcon = notAlive ? icons[icons.length - 1] : icons[indice];
            return (
              <Marker
                key={`${asset.name}_marker`}
                position={asset.centroid}
                icon={markerIcon}
                eventHandlers={{
                  popupopen: (e) => handlePopupOpen(e, asset, alarmCount),
                }}
              >
                <Popup className={"custom-popup"} key={`${asset.name}_popup`}>
                  <AlertPopup
                    key={`${asset.name}_popup_content`}
                    alarmCount={alarmCount}
                    alarmLabel={intl.formatMessage({ id: "structure" })}
                    alarmName={asset.name}
                    name={asset.name}
                    image={asset.image}
                    equipments={equipments}
                  />
                </Popup>
              </Marker>
            );
          })}
        {anyDetailView &&
          inclinometerList.map((inclinometer, incIndex) => (
            <Marker
              key={`inclinometer_${inclinometer.name}`}
              position={inclinometer.polyline}
              pane="markersPane"
              icon={incIcon}
            >
              <Popup>
                <div>
                  <div style={{ display: "flex", alignItems: "center", marginBottom: "0.1rem", gap: "0.5rem" }}>
                    <span style={{ fontWeight: "600" }}>{intl.formatMessage({ id: "name" })}:</span>
                    <span>{inclinometer.name}</span>
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        {!isNil(currentZoom) &&
          !isNil(ships) &&
          !isEmpty(ships) &&
          anyDetailView &&
          ships.map((ship) => {
            const shipType =
              !isNil(ship.shipInfo.type) && shipTypes.indexOf(ship.shipInfo.type) >= 0
                ? ship.shipInfo.type
                : "unspecified";

            const found = shipsTracking?.filter((alert) => alert.shipId === ship.shipId);
            let extra = "";
            if (!isNil(found) && !isEmpty(found) && found[0].typeArea === "critical") {
              extra = `danger`;
            }
            if (!isNil(found) && !isEmpty(found) && found[0].typeArea === "protection") {
              extra = `protection`;
            }
            let newLastDate = "",
              activeSince = "";
            if (!isNil(shipsStatus) && !isEmpty(shipsStatus)) {
              if (!isNil(shipsStatus.active[ship.shipId])) {
                newLastDate = shipsStatus.active[ship.shipId].last_seen;
                activeSince = shipsStatus.active[ship.shipId].active_since;
              }
            }

            return (
              <React.Fragment key={`${ship.shipId}_div`}>
                {selectedShip === ship.shipId && (
                  <Marker
                    key={`${ship.shipId}_shadow`}
                    icon={customShadowIcon}
                    pane="markerPane"
                    zIndexOffset={-200}
                    position={[ship.location[1], ship.location[0]]}
                  ></Marker>
                )}
                <Marker
                  key={`${ship.shipId}`}
                  icon={createShipIcon(shipType, headings[ship.direction], extra, bearing)}
                  position={[ship.location[1], ship.location[0]]}
                  pane="markersPane"
                  ref={(ref) => {
                    if (ref) markerRefs.current[ship.shipId] = ref;
                  }}
                  eventHandlers={{
                    popupopen: () => setOpenPopupShipId(ship.shipId),
                    popupclose: () => {
                      if (openPopupShipId === ship.shipId) {
                        setOpenPopupShipId(null);
                      }
                    },
                  }}
                >
                  <Popup autoClose={false}>
                    <div>
                      <h1>MMSI: {ship.shipId}</h1>
                      <h1>
                        {intl.formatMessage({ id: "name" })}: {ship.shipInfo.name}
                      </h1>
                      <h2>
                        {intl.formatMessage({ id: "type" })}: {shipType}
                      </h2>
                      <h2>
                        {intl.formatMessage({ id: "speed" })}: {ship.speed}
                      </h2>
                      {activeSince !== "" && (
                        <h2>
                          {intl.formatMessage({ id: "last.seen" })}: {activeSince}
                        </h2>
                      )}
                      {selectedShip === ship.shipId && isNil(found) && isEmpty(found) && (
                        <IconButton
                          id={`${ship.shipId}button_poly_clear_avoid`}
                          size="large"
                          color="inherit"
                          onClick={async () => {
                            setSelectedShip(null);
                            setShipHistory(null);
                          }}
                          sx={{
                            backgroundColor: palette.primary,
                            color: "white",
                            borderRadius: "50%",
                            boxShadow: "0 3px 6px rgba(0,0,0,0.3)",
                            "&:hover": {
                              backgroundColor: palette.primary,
                              filter: "brightness(0.9)",
                            },
                            "&:active": {
                              boxShadow: "0 1px 3px rgba(0,0,0,0.4)",
                              transform: "scale(0.95)",
                            },
                          }}
                        >
                          <ClearIcon />
                        </IconButton>
                      )}
                      {!isNil(found) && !isEmpty(found) && (
                        <table
                          style={{ whiteSpace: "nowrap", borderSpacing: "0", border: `1px solid ${palette.primary}` }}
                        >
                          <thead>
                            <tr
                              style={{
                                textAlign: "center",
                                backgroundColor: palette.primary,
                                color: palette.likeWhite,
                              }}
                            >
                              <td>{intl.formatMessage({ id: "date.start" })}</td>
                              <td>{intl.formatMessage({ id: "date.end" })}</td>
                              <td>{intl.formatMessage({ id: "type" })}</td>
                              <td>{intl.formatMessage({ id: "action" })}</td>
                            </tr>
                          </thead>
                          <tbody>
                            {found.map((alert, alertIndex) => (
                              <tr key={`${ship.shipId}alertRow${alertIndex}`}>
                                <td
                                  style={{
                                    padding: "0.1rem 0.5rem",
                                  }}
                                >
                                  {alert?.startTime?.substring(0, alert?.startTime?.indexOf(".")).replace("T", " ")}
                                </td>
                                <td
                                  style={{
                                    padding: "0.1rem 0.5rem",
                                  }}
                                >
                                  {newLastDate === ""
                                    ? alert?.lastUpdated
                                        ?.substring(0, alert?.lastUpdated?.indexOf("."))
                                        .replace("T", " ")
                                    : newLastDate.substring(0, alert?.lastUpdated?.indexOf(".")).replace("T", " ")}
                                </td>
                                <td
                                  style={{
                                    padding: "0.1rem 0.5rem",
                                  }}
                                >
                                  {!isNil(alert) &&
                                    !isNil(alert.typeArea) &&
                                    intl.formatMessage({ id: alert.typeArea })}
                                </td>

                                {alertIndex === 0 && (
                                  <td
                                    style={{
                                      padding: "0.1rem 0.5rem",
                                    }}
                                    rowSpan={found.length}
                                  >
                                    <IconButton
                                      id={`${ship.shipId}button_poly${alertIndex}`}
                                      size="large"
                                      color="inherit"
                                      onClick={async () => {
                                        if (selectedShip !== ship.shipId) {
                                          setSelectedShip(ship.shipId);
                                          const fecha = new Date(alert.lastUpdated);
                                          fecha.setHours(fecha.getHours() - 24);
                                          const startTime = fecha.toISOString();
                                          const response = await getShipTrackingHistorial(
                                            alert.trackingId,
                                            ship.shipId,
                                            startTime,
                                            alert.lastUpdated,
                                          );
                                          setShipHistory(response.data.data);
                                        } else {
                                          setSelectedShip(null);
                                          setShipHistory(null);
                                        }
                                      }}
                                      sx={{
                                        backgroundColor: palette.primary,
                                        color: "white",
                                        borderRadius: "50%",
                                        boxShadow: "0 3px 6px rgba(0,0,0,0.3)",
                                        "&:hover": {
                                          backgroundColor: palette.primary,
                                          filter: "brightness(0.9)",
                                        },
                                        "&:active": {
                                          boxShadow: "0 1px 3px rgba(0,0,0,0.4)",
                                          transform: "scale(0.95)",
                                        },
                                      }}
                                    >
                                      {selectedShip !== ship.shipId ? <PolylineIcon /> : <ClearIcon />}
                                    </IconButton>
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </Popup>
                </Marker>
              </React.Fragment>
            );
          })}
        {!isNil(currentZoom) &&
          !isNil(ships) &&
          !isNil(shipHistory) &&
          anyDetailView &&
          shipHistory.map((ruta, rutaIndex) => {
            return ruta.polylines.map((polyline, polylineLineIndex) => {
              return polyline.coords.map((line, lineIndex) => {
                if (lineIndex === 0) return null;
                return (
                  <Polyline
                    key={`${alert.application}-${alert.asset}-${polylineLineIndex}-${lineIndex}_ruta${rutaIndex}`}
                    positions={[
                      [polyline.coords[lineIndex - 1][0][0], polyline.coords[lineIndex - 1][0][1]],
                      [line[0][0], line[0][1]],
                    ]}
                    weight={
                      currentZoom === 11 || currentZoom === 12 ? 8 : currentZoom === 13 || currentZoom === 14 ? 6.5 : 5
                    }
                    color={"black"}
                    pane="linesPane"
                  ></Polyline>
                );
              });
            });
          })}

        {loading && isEmpty(polylines) && (
          <div
            style={{
              position: "absolute",
              width: "100%",
              height: "100%",
              backgroundColor: "rgb(176 176 176 / 60%)",
              zIndex: 10000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            <h1>{`${intl.formatMessage({ id: "loading.points" })}...`}</h1>
          </div>
        )}
        {!isNil(polygonCritical) &&
          !isEmpty(polygonCritical) &&
          !hidePolygons &&
          polygonCritical.map((crit, polyIndex) => (
            <Polygon positions={crit} key={`crit${polyIndex}`} pane="polygonsPane" pathOptions={{ color: "red" }} />
          ))}
        {!isNil(polygonAvoid) &&
          !isEmpty(polygonAvoid) &&
          // eslint-disable-next-line array-callback-return
          !hidePolygons &&
          polygonAvoid.map((avoid, polyIndex) => {
            if (avoid.geometry.type === "Polygon") {
              return (
                <Polygon
                  positions={avoid.geometry.coordinates}
                  key={`avoid${polyIndex}`}
                  pane="polygonsPane"
                  pathOptions={{ color: "white" }}
                />
              );
            } else if (avoid.geometry.type === "Point") {
              return (
                <Circle
                  key={`avoid${polyIndex}`}
                  radius={avoid.distance}
                  center={avoid.geometry.coordinates}
                  pane="polygonsPane"
                  pathOptions={{ color: "white" }}
                />
              );
            }
          })}
        {!isNil(polygonExtra) &&
          !isEmpty(polygonExtra) &&
          !hidePolygons &&
          // eslint-disable-next-line array-callback-return
          polygonExtra.map((extra, polyIndex) => {
            if (extra.geometry.type === "polygon") {
              return (
                <Polygon
                  positions={extra.geometry.coordinates}
                  key={`extra${polyIndex}`}
                  pane="polygonsPane"
                  pathOptions={{ color: "red" }}
                />
              );
            } else if (extra.geometry.type === "Point") {
              return (
                <Circle
                  key={`extra${polyIndex}`}
                  radius={extra.distance}
                  center={extra.geometry.coordinates}
                  pane="polygonsPane"
                  pathOptions={{ color: "red" }}
                />
              );
            }
          })}
        {!isNil(polygonProtect) &&
          !isEmpty(polygonProtect) &&
          !hidePolygons &&
          polygonProtect.map((prot, polyIndex) => (
            <Polygon positions={prot} key={`prot${polyIndex}`} pane="polygonsPane" pathOptions={{ color: "green" }} />
          ))}

        {!isNil(currentZoom) &&
          !isEmpty(polylines) &&
          (anyDetailView
            ? printPolylineAlerts({
                configData,
                currentZoom,
                assetsAlerts: computedAssetsAlerts,
                assetsAlertsFilter: computedAssetsAlertsFilter,
                selectedAssetsAlertsFilter,
                polylines,
                assets,
                selectedSubs,
                assetEquipments,
                selectedEquipment,
                inside,
                intl,
                points,
                customIcon: createAlertIcon,
                createAlertIconGif,
                navigate,
              })
            : null)}
        {!isNil(currentZoom) &&
          (anyDetailView
            ? !isNil(assets) &&
              assets
                .filter(
                  (a) =>
                    !isNil(a.equipments) &&
                    (filter === "status" || filter === "status.secondary" || inside.includes(a.name)) &&
                    detailViewMap[a.name],
                )
                ?.map((a, aIndex) => {
                  return printEquipments(
                    `${a.name}-equipment-${aIndex}"`,
                    a.equipments,
                    customEquipmentIcon,
                    customEquipmentAlertIcon,
                    alerts,
                    intl,
                  );
                })
            : null)}
        {!isNil(assetsOptions) && anyDetailView && (
          <LegendContent>
            <h2 onClick={() => setLeftOpen(!leftOpen)} style={{ marginLeft: "4%" }}>
              {intl.formatMessage({ id: "data.visualization" }).toUpperCase()}{" "}
              {leftOpen ? (
                <ArrowDropUpIcon sx={{ paddingTop: "1%" }} />
              ) : (
                <ArrowDropDownIcon sx={{ paddingTop: "1%" }} />
              )}
            </h2>
            <Collapse in={leftOpen}>
              <TextField
                id="status"
                margin="dense"
                style={{ width: "10.5rem", margin: "2%" }}
                variant="outlined"
                value={selectedType}
                onChange={(e) => {
                  const selection = e.target.value;
                  const measure = currentAssetMeasure[selection];
                  setCurrentAssetValues(assetsValues.data[measure]);

                  setRange(scales[measure].range());
                  setDomain(scales[measure].domain());
                  setSelectedType(selection);
                  setFilter(measure);
                  if (measure === "status" || measure === "status.secondary") {
                    setMapCentroid(getCentroid(assets?.map((item) => item.centroid)));
                    setFiltered_Asset("");
                  } else {
                    if (filtered_asset === "") {
                      setFiltered_Asset(assets?.[0]?.name);
                      setMapCentroid(assets?.[0]?.centroid);
                    }
                  }
                  setReset(true);
                }}
                select
              >
                {currentAssetMeasure.map((mg, i) => (
                  <MenuItem key={`${mg}_current_select_${i}`} value={i}>
                    {`${intl.formatMessage({ id: mg })}`}
                  </MenuItem>
                ))}
              </TextField>
              {!isNil(selectedEquipment) && !isNil(assetEquipments) && (
                <TextField
                  id="status"
                  margin="dense"
                  style={{ width: "10.5rem", margin: "2%" }}
                  variant="outlined"
                  value={selectedEquipment}
                  defaultValue={selectedEquipment}
                  onChange={(e) => {
                    selectEquipment(
                      e.target.value,
                      assetEquipments,
                      setFiltered_Asset,
                      setMeasureMinus,
                      setStateMagnitude,
                      setSelectedEquipment,
                      setIndexEquipmentChannel,
                      setSelectedChannels,
                      setFilter,
                      setCurrentAsset,
                      autocompleteRef,
                    );
                  }}
                  select
                >
                  {assetEquipments.reduce(
                    (acc, ae) => [
                      ...acc,
                      ...ae.map((equipment, innerIndex) => {
                        const globalIndex = acc.length + innerIndex;
                        return (
                          <MenuItem
                            disabled={!visible[globalIndex]}
                            key={`${equipment.equipmentID}_current_select_${globalIndex}`}
                            value={equipment.equipmentID}
                          >
                            {equipment.name}
                          </MenuItem>
                        );
                      }),
                    ],
                    [],
                  )}
                </TextField>
              )}

              {!isNil(equipmentSubs[indexEquipmentChannel]) &&
                equipmentSubs[indexEquipmentChannel].length > 0 &&
                (() => {
                  const visualOptions = subList[indexEquipmentChannel];
                  //value={
                  //  selectedSubs[indexEquipmentChannel] && selectedSubs[indexEquipmentChannel].length >= 1
                  //    ? selectedSubs[indexEquipmentChannel]
                  //        .map((channel) => channel + 1) // Convertir de 0 a 1
                  //        .filter((option) => currentVisibilityArray[option - 1]) // Filtrar por visibilidad
                  //    : []
                  //}
                  return (
                    <Autocomplete
                      multiple
                      id="multiple-checkboxes"
                      options={
                        visualOptions // Filtrar según el visibilityArray
                      }
                      disableCloseOnSelect
                      onChange={(event, values) => {
                        let newSelected = clone(selectedSubs);
                        newSelected[indexEquipmentChannel] = values;
                        setSelectedSubs(newSelected);
                      }}
                      getOptionLabel={(option) => option.name}
                      value={
                        selectedSubs[indexEquipmentChannel] && selectedSubs[indexEquipmentChannel].length >= 1
                          ? selectedSubs[indexEquipmentChannel].map((sub) => sub)
                          : []
                      }
                      isOptionEqualToValue={(option, value) => option.name === value.name}
                      renderOption={(props, option, { selected }) => {
                        return (
                          <li {...props}>
                            <Checkbox style={{ marginRight: 8 }} checked={selected} />
                            {option.name}
                          </li>
                        );
                      }}
                      style={{ width: "10.5rem", margin: "2%" }}
                      renderInput={(params) => (
                        <TextField {...params} variant="outlined" label={intl.formatMessage({ id: "select.subs" })} />
                      )}
                      ref={autocompleteRef}
                    />
                  );
                })()}
              {!isNil(polylines) && !isEmpty(polylines) && (
                <Legend
                  minValue={0}
                  maxValue={1}
                  assets={assets}
                  scales={scales}
                  filtered_asset={filtered_asset}
                  measure_filter={filter}
                  setFilter={setFilter}
                  currentAssetMeasure={currentAssetMeasure}
                  setSelectedType={setSelectedType}
                  currentAssetValues={currentAssetValues}
                  intl={intl}
                  assetsValues={assetsValues}
                  selectedSubs={selectedSubs}
                  selectedChannels={selectedChannels}
                  selectedEquipment={selectedEquipment}
                  assetEquipments={assetEquipments}
                  equipmentTypes={equipmentTypes}
                  visible={visible}
                  inside={inside}
                />
              )}
              {!isNil(polygonCritical) && !isEmpty(polygonCritical) && (
                <>
                  <FormControlLabel
                    control={
                      <Checkbox
                        id={"checkbox_polygon_hider"}
                        sx={{
                          color: palette.primary,
                          "&.Mui-checked": {
                            color: palette.primary,
                          },
                          marginLeft: "7%",
                          marginTop: "7%",
                        }}
                        defaultChecked={hidePolygons}
                        onClick={(e) => {
                          setHidePolygons(e.target.checked);
                        }}
                      />
                    }
                    label={intl.formatMessage({ id: "hide.polygons" })}
                  />
                  {userData.role === "total_admin" && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          id={"checkbox_simulate_ship"}
                          sx={{
                            color: palette.primary,
                            "&.Mui-checked": {
                              color: palette.primary,
                            },
                            marginLeft: "7%",
                            marginTop: "5%",
                          }}
                          onClick={async (e) => {
                            await simulateShip();
                            if (!e.target.checked) {
                              let newShips = clone(ships);
                              newShips = newShips.filter((ship) => ship.shipId !== 777777000);
                              setShips(newShips);
                              if (selectedShip === 777777000) {
                                setSelectedShip(null);
                                setShipHistory(null);
                              }
                            }
                          }}
                        />
                      }
                      label={intl.formatMessage({ id: "simulate.ship" })}
                    />
                  )}
                </>
              )}
            </Collapse>
          </LegendContent>
        )}
        {!isEmpty(computedAssetsAlertsFilter) && anyDetailView && (
          <LegendContentRight>
            {!isEmpty(computedAssetsAlertsFilter) &&
              (() => {
                return (
                  <>
                    <h2 onClick={() => setRightOpen(!rightOpen)} style={{ marginLeft: "4%" }}>
                      {intl.formatMessage({ id: "alert.visualization" }).toUpperCase()}{" "}
                      {rightOpen ? (
                        <ArrowDropUpIcon sx={{ paddingTop: "1%" }} />
                      ) : (
                        <ArrowDropDownIcon sx={{ paddingTop: "1%" }} />
                      )}
                    </h2>
                    <Collapse in={rightOpen}>
                      <Autocomplete
                        multiple
                        id="multiple-checkboxes"
                        options={computedAssetsAlertsFilter}
                        disableCloseOnSelect
                        onChange={(event, values) => {
                          setSelectedAssetsAlertsFilter(values);
                        }}
                        getOptionLabel={(option) => {
                          const index = option.indexOf("-");
                          const part1 = option.substring(0, index);
                          const part2 = option.substring(index + 1);
                          return `${intl.formatMessage({ id: part1 })}-${intl.formatMessage({ id: part2 })}`;
                        }}
                        value={selectedAssetsAlertsFilter.length >= 1 ? selectedAssetsAlertsFilter.map((af) => af) : []}
                        renderOption={(props, option, { selected }) => {
                          const index = option.indexOf("-");
                          const part1 = option.substring(0, index);
                          const part2 = option.substring(index + 1, option.length);
                          return (
                            <li {...props}>
                              <Checkbox
                                style={{ marginRight: 8 }}
                                checked={
                                  selectedAssetsAlertsFilter.length > 0
                                    ? selectedAssetsAlertsFilter.some((af) => af === option)
                                    : false
                                }
                              />
                              {intl.formatMessage({ id: part1 })}-{intl.formatMessage({ id: part2 })}
                            </li>
                          );
                        }}
                        style={{ width: "16.5rem", margin: "2%" }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            label={intl.formatMessage({ id: "select.alerts" })}
                          />
                        )}
                      />
                    </Collapse>
                  </>
                );
              })()}
          </LegendContentRight>
        )}
        {window.RUNTIME_CONFIG.IS_ONLINE ? (
          <MapTypeContent>
            <MapTypeSelector items={window.RUNTIME_CONFIG.MAP_TYPES} onChange={setMapType} color={palette.dark} />
          </MapTypeContent>
        ) : null}
      </MapContainer>
    )
  );
};

export default Map;
